import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import "./cycleConfirmationModal.css";
import { GET_MAINT_CYCLE_INFERENCE_FOR_TASK } from "../../../../utils/GraphQL/queries";
import {
  CONFIRM_MAINT_CYCLE,
  SEND_ONE_MAINT_REMINDER,
} from "../../../../utils/GraphQL/mutations";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

const CYCLE_LABELS = {
  1: "Maintenance #1 (refinish was the spawning job)",
  2: "Maintenance #2",
  3: "Maintenance #3",
  4: "Maintenance #4",
  5: "5+ — Door has had 4 maintenances; Refinish Reminder will be sent (cap)",
};

const CycleConfirmationModal = ({ open, onClose, taskId, onAfterAction }) => {
  const { handleSnack } = useSnackbar();
  const [loadInference, { data, loading, error }] = useLazyQuery(
    GET_MAINT_CYCLE_INFERENCE_FOR_TASK,
    { fetchPolicy: "network-only" }
  );
  const [confirmMutation, { loading: confirming }] = useMutation(
    CONFIRM_MAINT_CYCLE
  );
  const [sendOneMutation, { loading: sending }] = useMutation(
    SEND_ONE_MAINT_REMINDER
  );

  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    if (open && taskId) {
      setSelectedPosition(null);
      loadInference({ variables: { taskId } });
    }
  }, [open, taskId, loadInference]);

  const inference = data?.getMaintenanceCycleInferenceForTask;

  const isEditable = useMemo(() => {
    if (!inference) return false;
    if (inference.assigneeId !== "11") return false;
    if (!inference.taskType || !/reminder/i.test(inference.taskType)) return false;
    if (inference.taskActive === false) return false;
    return true;
  }, [inference]);

  const notEditableReason = useMemo(() => {
    if (!inference) return null;
    if (inference.taskActive === false) {
      return "This task is marked completed. Restore it from the task page if you need to edit cycle position.";
    }
    if (inference.assigneeId !== "11") {
      return "This reminder has already fired — the task is now a follow-up (F/U). Cycle position is read-only.";
    }
    if (!inference.taskType || !/reminder/i.test(inference.taskType)) {
      return "This task is no longer a maintenance reminder. Cycle position is read-only.";
    }
    return null;
  }, [inference]);

  // Pre-select the system suggestion when it lands, but only if user hasn't
  // already picked something AND the task is editable. Lets office accept the
  // suggestion with one click in the common case.
  useEffect(() => {
    if (!isEditable) return;
    if (
      inference?.suggestedPosition &&
      selectedPosition == null &&
      typeof inference.suggestedPosition === "number"
    ) {
      setSelectedPosition(
        inference.suggestedPosition >= 5 ? 5 : inference.suggestedPosition
      );
    }
  }, [inference, selectedPosition, isEditable]);

  const sortedTimeline = useMemo(() => {
    if (!inference?.candidateInvoices) return [];
    return [...inference.candidateInvoices];
  }, [inference]);

  // Hide "Confirm & Send Now" when the anchor invoice's job is still in the
  // future. Sending a "X months since refinish" email when the job hasn't
  // happened yet produced "-1 months" copy on 2026-05-11. The server has a
  // hard guard too — this is the user-facing half so office never sees a
  // tempting "Send Now" button in a context where send is structurally wrong.
  const anchorIsFuture = useMemo(() => {
    const current = inference?.candidateInvoices?.find((c) => c.isCurrent);
    if (!current?.jobScheduledFor) return false;
    return parseInt(current.jobScheduledFor) > Date.now();
  }, [inference]);

  const handleConfirm = async (alsoSend = false) => {
    if (selectedPosition == null) {
      handleSnack && handleSnack("Pick a cycle position first", 3000);
      return;
    }
    try {
      await confirmMutation({
        variables: { taskId, cyclePosition: selectedPosition },
      });
      if (alsoSend) {
        const dateMills = Date.now();
        const offsetMills =
          new Date(dateMills).getTimezoneOffset() * 60 * 1000;
        await sendOneMutation({
          variables: {
            taskId,
            currentDate: dateMills.toString(),
            offsetMills: offsetMills.toString(),
          },
        });
        handleSnack && handleSnack("Confirmed and sent", 3000);
      } else {
        handleSnack && handleSnack("Cycle position confirmed", 3000);
      }
      onAfterAction && onAfterAction();
      onClose();
    } catch (err) {
      handleSnack && handleSnack(`Error: ${err.message}`, 5000);
    }
  };

  return (
    /*
     * LAYER. This used to hand-pick `z-index: 1000003` — one above
     * --bdr-z-confirm — so it sat on top of even a confirm dialog. `peak` is
     * the top modal rung and is BELOW a confirm, which is the correct order:
     * a confirm should cover the thing that raised it, not the other way
     * round. It still clears every modal that can host it (the quote and
     * invoice modals, job-form review).
     */
    <BdrModal
      open={open}
      onClose={onClose}
      layer="peak"
      variant="panel"
      title="Maintenance Cycle Position"
      actions={
        <>
          <BdrButton onClick={onClose} disabled={confirming || sending}>
            {isEditable ? "Cancel" : "Close"}
          </BdrButton>
          {isEditable && (
            <>
              <BdrButton
                variant="confirm"
                onClick={() => handleConfirm(false)}
                disabled={confirming || sending || selectedPosition == null}
              >
                {confirming ? "Saving..." : "Confirm"}
              </BdrButton>
              {!anchorIsFuture && (
                /* The action-plus-automation one is the primary: it does
                   everything Confirm does and then sends. */
                <BdrButton
                  variant="confirm-solid"
                  onClick={() => handleConfirm(true)}
                  disabled={confirming || sending || selectedPosition == null}
                  title={
                    selectedPosition >= 5
                      ? "Cap reached — sends the Refinish Reminder email and hands the task to office F/U (due 3rd business day)"
                      : undefined
                  }
                >
                  {sending ? "Sending..." : "Confirm & Send Now"}
                </BdrButton>
              )}
            </>
          )}
        </>
      }
    >
        {loading && <AdminBDRSpinner message="Loading inference..." />}

        {error && (
          <div className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
            Error loading inference: {error.message}
          </div>
        )}

        {inference && !loading && (
          <>
            <div className="bdr-modal-section cycle-confirm-section">
              <div>
                <span className="green-color-text">Client:</span>{" "}
                {inference.associatedClientName || "(unknown)"}
              </div>
              {inference.invoiceNum && (
                <div>
                  <span className="green-color-text">This reminder is for:</span>{" "}
                  Invoice #{inference.invoiceNum}
                </div>
              )}
              <div>
                <span className="green-color-text">System guess:</span>{" "}
                {inference.suggestedPosition != null
                  ? `cycle position ${inference.suggestedPosition}`
                  : "could not determine"}{" "}
                ({inference.confidence})
              </div>
              {typeof inference.currentCyclePosition === "number" && (
                <div>
                  <span className="green-color-text">Currently set to:</span>{" "}
                  cycle position {inference.currentCyclePosition}
                </div>
              )}
              <div className="cycle-confirm-reason">{inference.reason}</div>
            </div>

            {notEditableReason && (
              <div className="cycle-confirm-readonly-notice">
                {notEditableReason}
              </div>
            )}

            {sortedTimeline.length > 0 && (
              <div className="bdr-modal-section cycle-confirm-section">
                <div className="green-color-text" style={{ marginBottom: 4 }}>
                  Client invoice history:
                </div>
                <ul className="cycle-confirm-timeline">
                  {sortedTimeline.map((entry) => {
                    const dateStr = entry.jobScheduledFor
                      ? BAGroupTimeFormat(entry.jobScheduledFor, false, true)
                      : "(no date)";
                    return (
                      <li
                        key={entry.invoiceId}
                        className={`cycle-confirm-timeline-item${
                          entry.isCurrent ? " is-current" : ""
                        }${entry.isAnchor ? " is-anchor" : ""}`}
                      >
                        <span className="cycle-confirm-timeline-date">
                          {dateStr}
                        </span>
                        <span className="cycle-confirm-timeline-num">
                          Inv #{entry.invoiceNum || "?"}
                        </span>
                        <span className="cycle-confirm-timeline-type">
                          {entry.derivedJobType}
                        </span>
                        {entry.isAnchor && (
                          <span className="cycle-confirm-tag-anchor">
                            anchor
                          </span>
                        )}
                        {entry.isCurrent && (
                          <span className="cycle-confirm-tag-current">
                            this reminder
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {isEditable && (
              <div className="bdr-modal-section cycle-confirm-section">
                <div className="green-color-text" style={{ marginBottom: 4 }}>
                  Cycle position for this task:
                </div>
                {[1, 2, 3, 4, 5].map((pos) => (
                  <label
                    key={pos}
                    className={`cycle-confirm-radio-row${
                      selectedPosition === pos ? " selected" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="cyclePosition"
                      value={pos}
                      checked={selectedPosition === pos}
                      onChange={() => setSelectedPosition(pos)}
                    />
                    <span>{CYCLE_LABELS[pos]}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Stays in the BODY, not the action row: it explains why a
                button is absent, so it belongs with the content. */}
            {isEditable && anchorIsFuture && (
              <div className="cycle-confirm-future-note">
                This reminder's job is scheduled for the future. The
                maintenance hasn't happened yet, so no reminder email can be
                sent now. The task will fire automatically when due.
              </div>
            )}
          </>
        )}
    </BdrModal>
  );
};

export default CycleConfirmationModal;
